import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Checkbox, notification, Typography } from 'antd'
import useMutation from 'hooks/useMutation'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'

type CopyOptionsType = {
  arrows: boolean
  notes?: boolean
  documents?: boolean
  annotations?: boolean
}

export default function DetailCopyModal({
  handleCancel,
  buildingId,
  handleReload,
  buildingType
}: {
  handleCancel: () => void
  buildingId: number
  handleReload: () => void
  buildingType: number
}) {
  const { t } = useTranslation()

  const { pathname } = useLocation()

  const { trigger, isMutating } = useMutation(
    `/v1/buildings/${buildingId}/duplicate`
  )
  const [isCheckCopyArrow, setIsCheckCopyArrow] = useState(true)
  const [isCheckCopyNote, setIsCheckCopyNote] = useState(true)
  const [isCheckCopyAnnotation, setIsCheckCopyAnnotation] = useState(true)
  const [isCheckCopyFile, setIsCheckCopyFile] = useState(true)
  const [isCopySuccess, setIsCopySuccess] = useState(false)

  const isShowTourOptions = useMemo(
    () => pathname.includes('tour') && buildingType !== 10,
    [pathname, buildingType]
  )

  const handleCopyBuilding = () => {
    const copyOptions: CopyOptionsType = { arrows: isCheckCopyArrow }

    if (isShowTourOptions) {
      copyOptions.notes = isCheckCopyNote
    } else {
      copyOptions.annotations = isCheckCopyAnnotation
      copyOptions.documents = isCheckCopyFile
    }

    const data = {
      target_building_type: buildingType,
      copy_options: copyOptions
    }

    !isMutating &&
      trigger(
        {
          method: 'post',
          data,
          successMessage: 'organization.copySuccessNotification'
        },
        {
          onSuccess: () => {
            setIsCopySuccess(true)
            handleReload()
          },
          onError: (e) => {
            if (e.response.status === 403) {
              notification.error({
                message: t('organization.copyFailFailedWithoutPermission'),
                key: t('organization.copyFailFailedWithoutPermission')
              })
            } else if (e.response.status === 404) {
              notification.error({
                message: t('organization.copyFailBuildingNotFound'),
                key: t('organization.copyFailBuildingNotFound')
              })
            } else {
              notification.error({
                message: t('organization.copyFailNotification'),
                key: t('organization.copyFailNotification')
              })
            }
          }
        }
      )
  }

  const handleModalClose = () => {
    if (isCopySuccess) {
      setIsCopySuccess(false)
      handleCancel()
    } else {
      handleCancel()
    }
  }

  return (
    <Modal width="480px" onCancel={handleModalClose} isEnterable={false}>
      <Spinner spinning={isMutating}>
        <div className="text-center flex flex-col gap-[30px] items-center">
          {!isCopySuccess ? (
            <>
              <div className="flex flex-col justify-center items-center">
                <Typography className="text-[20px] font-[700] theme-text-primary">
                  {t(`building.detail-copy-title`)}
                </Typography>
                <div className="border-0 border-b border-solid theme-border-primary w-10 m-auto mt-[10px] mb-[14px]" />
              </div>

              <div className="grid grid-cols-[1fr_auto] gap-y-10 gap-x-14 items-center">
                <span className="whitespace-nowrap">{t('building.arrow')}</span>
                <Checkbox
                  checked={isCheckCopyArrow}
                  onChange={() => setIsCheckCopyArrow(!isCheckCopyArrow)}
                />
                {isShowTourOptions ? (
                  <>
                    <span className="whitespace-nowrap">
                      {t('building.notes')}
                    </span>
                    <Checkbox
                      checked={isCheckCopyNote}
                      onChange={() => setIsCheckCopyNote(!isCheckCopyNote)}
                    />
                  </>
                ) : (
                  <>
                    <span className="whitespace-nowrap">
                      {t('building.document')}
                    </span>
                    <Checkbox
                      checked={isCheckCopyAnnotation}
                      onChange={() =>
                        setIsCheckCopyAnnotation(!isCheckCopyAnnotation)
                      }
                    />
                    <span className="whitespace-nowrap">
                      {t('building.file')}
                    </span>
                    <Checkbox
                      checked={isCheckCopyFile}
                      onChange={() => setIsCheckCopyFile(!isCheckCopyFile)}
                    />
                  </>
                )}
              </div>

              <div className="flex gap-10 justify-center w-full items-end">
                <Button onClick={handleModalClose} sizing="w-[140px] h-[50px]">
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleCopyBuilding}
                  sizing="w-[140px] h-[50px]"
                  type="primary"
                >
                  {t('common.ok')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col justify-center items-center">
                <Typography className="text-[20px] font-[700] theme-text-primary mb-4">
                  {t('building.copy-success-title')}
                </Typography>
                <Typography className="text-base text-gray-600 text-center max-w-[360px]">
                  {t('building.copy-success-message')}
                </Typography>
                <Typography className="text-base text-gray-600 mt-2">
                  {t('building.copy-time-estimate')}
                </Typography>
                <Typography className="text-base text-gray-600 mt-2">
                  {t('building.copy-note')}
                </Typography>
              </div>

              <div className="flex justify-center w-full">
                <Button
                  onClick={handleModalClose}
                  sizing="w-[140px] h-[50px]"
                  type="primary"
                >
                  {t('common.ok')}
                </Button>
              </div>
            </>
          )}
        </div>
      </Spinner>
    </Modal>
  )
}
